"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.FulfillableItemList = void 0;
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));

const isEqual_1 = __importDefault(require("lodash/isEqual"));

const types_1 = require("./types");

const {
  FULFILLABLE_ITEM_CHARGING_MODEL_CAP_UNLIMITED,
  FULFILLABLE_ITEM_CHARGING_MODEL_TYPE,
  FULFILLABLE_ITEM_CODE,
  FULFILLABLE_ITEM_TYPE,
  FULFILLABLE_ITEM_DELEGATION_TYPE
} = types_1.FULFILLABLE_ITEM_CONSTANTS;

class FulfillableItemList {
  /**
   * @description Creates a new FulfillableItemList for use. The whole class is still work in progress.
   *
   * @param {Array} items Fulfillable Items which make up this list
   */
  constructor(items = []) {
    this.items = [];
    this.savedState = [];
    this.items = parse(items);
    this.registerSavedState();
  }
  /**
   * @description Updates model with a nested form of itself recording state
   *     which may be later modified.
   */


  registerSavedState() {
    this.savedState = cloneDeep_1.default(this.items);
  }
  /**
   * @description Method to return consumable fulfillable items of quota type.
   * @param {Object} options - options for getting the items
   * @param {Boolean} [options.includeDelegationTypePerson] true if configurable items
   *   returned should include a person with the delegation type. Defaults to true.
   * @param {Boolean} [options.requireDelegationConfigurable] true if only configurable items
   *   should be returned. Defaults to true.
   *
   * @returns {Array} consumable quotas FIs in the list
   */


  getConsumableQuotaTypeItems(options = {}) {
    const {
      includeDelegationTypePerson = true,
      requireDelegationConfigurable = true
    } = options;
    return this.items.filter(item => item.fulfillableItemType === FULFILLABLE_ITEM_TYPE.QUOTA && !types_1.QUOTA_IGNORE_LIST.includes(item.code) && (!requireDelegationConfigurable || item.delegationConfigurable) && (includeDelegationTypePerson || item.delegationType !== FULFILLABLE_ITEM_DELEGATION_TYPE.PERSON));
  }
  /**
   * @description Method to return the cap for the cc_storage fulfillable item.
   *
   * @returns {number} storage charging model cap.
   */


  getCreativeCloudStorageChargingModelCap() {
    const item = this.items.find(i => i.code === FULFILLABLE_ITEM_CODE.CC_STORAGE);
    return extractQuotaChargingModelCap(item);
  }
  /**
   * @description Method to return fulfillable items of desktop type.
   *
   * @returns {Array} desktop FIs in the list
   */


  getDesktopTypeItems() {
    return this.items.filter(item => item.fulfillableItemType === FULFILLABLE_ITEM_TYPE.DESKTOP);
  }
  /**
   * @description Method to return the cap for the esm_user_storage fulfillable item
   *
   * @returns {Number} storage charging model cap
   */


  getESMUserStorageChargingModelCap() {
    const item = this.items.find(i => i.code === FULFILLABLE_ITEM_CODE.ESM_USER_STORAGE);
    return extractQuotaChargingModelCap(item);
  }
  /**
   * @description Method to return fulfillable items of organization type quota recurring
   *              UNLIMITED cap.
   *
   * @returns {FulfillableItem} the first (and should be only) organization UNLIMITED quota FulfillableItem in the list
   */


  getOrgDelegatableUnlimitedItem() {
    return this.items.find(item => item.delegationType === FULFILLABLE_ITEM_DELEGATION_TYPE.ORGANIZATION && item.fulfillableItemType === FULFILLABLE_ITEM_TYPE.QUOTA && item.chargingModel && item.chargingModel.cap === FULFILLABLE_ITEM_CHARGING_MODEL_CAP_UNLIMITED);
  }
  /**
   * @description Method to return fulfillable items of organization type quota on-demand consumable.
   *
   * @returns {FulfillableItem} the first (and should be only) organization on-demand quota FulfillableItem in the list
   */


  getOrgOnDemandConsumableItem() {
    return this.items.find(item => item.delegationType === FULFILLABLE_ITEM_DELEGATION_TYPE.ORGANIZATION && item.fulfillableItemType === FULFILLABLE_ITEM_TYPE.QUOTA && item.chargingModel && item.chargingModel.model === FULFILLABLE_ITEM_CHARGING_MODEL_TYPE.ON_DEMAND);
  }
  /**
   * @description Method to return fulfillable items of quota type.
   * @param {Boolean} [requireDelegationConfigurable] true if only configurable items
   *   should be returned. Defaults to true.
   *
   * @returns {Array<FulfillableItem>} quotas FIs in the list
   */


  getQuotaTypeItems(requireDelegationConfigurable = true) {
    return this.items.filter(item => item.fulfillableItemType === FULFILLABLE_ITEM_TYPE.QUOTA && (!requireDelegationConfigurable || item.delegationConfigurable));
  }
  /**
   * @description Method to return fulfillable items of service type for this Product instance.
   * @param {Boolean} [requireDelegationConfigurable] true if only configurable items
   *   should be returned. Defaults to true.
   *
   * @returns {Array<FulfillableItem>} services FIs in the list
   */


  getServiceTypeItems(requireDelegationConfigurable = true) {
    return this.items.filter(item => item.fulfillableItemType === FULFILLABLE_ITEM_TYPE.SERVICE && (!requireDelegationConfigurable || item.delegationConfigurable));
  }
  /**
   * @description Method to return a copy of the fulfillable items of service type for this Product instance
   *   with selected set to its initial value.
   *   This is used by the product profiles to initialize the configurable services in the profile.
   *
   * @returns {Array} all services FIs in the list with selected set to a Boolean
   */


  getServiceTypeItemsWithSelectedInitialized() {
    return this.getServiceTypeItems(false).map(service => {
      const result = cloneDeep_1.default(service);
      result.selected = result.purchased && !result.offByDefault;
      return result;
    });
  }
  /**
   * @description Method to return the cap for the Stock Credit fulfillable item
   *
   * @returns {Number} credit charging model cap
   */


  getStockCreditChargingModelCap() {
    const item = this.items.find(i => i.code === FULFILLABLE_ITEM_CODE.STOCK_CREDIT);
    return extractQuotaChargingModelCap(item);
  }
  /**
   * @description Method to return the cap for the Stock Image fulfillable item
   *
   * @returns {Number} image charging model cap
   */


  getStockImageChargingModelCap() {
    const item = this.items.find(i => i.code === FULFILLABLE_ITEM_CODE.STOCK_IMAGE);
    return extractQuotaChargingModelCap(item);
  }
  /**
   * @description Method to return stock related charging model caps.
   *
   * @returns {Array<ChargingModelCap>} stock charging model caps
   */


  getStockQuotas() {
    return this.items.filter(item => types_1.STOCK_QUOTAS.includes(item.code)).map(foundItem => ({
      chargingModelCap: extractQuotaChargingModelCap(foundItem),
      code: foundItem.code
    }));
  }
  /**
   * @description Method to return the cap for the Stock Video fulfillable item
   *
   * @returns {Number} video charging model cap
   */


  getStockVideoChargingModelCap() {
    const item = this.items.find(i => i.code === FULFILLABLE_ITEM_CODE.STOCK_VIDEO);
    return extractQuotaChargingModelCap(item);
  }
  /**
   * @description Method to determine if this list contains the asset sharing
   *   policy config FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasAssetSharingPolicyConfig() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.ASSET_SHARING_POLICY_CONFIG);
  }
  /**
   * @description Method to return whether the list has fulfillable items of consumable quota
   *              type.
   * @param {Object} options - options for getting the items
   * @param {Boolean} [options.requireDelegationConfigurable] true if only configurable items
   *   should be returned. Defaults to true.
   *
   * @returns {Boolean} true if any fulfillable item is of type consumable quota
   */


  hasConsumableQuotaTypeItems(options) {
    return this.getConsumableQuotaTypeItems(options).length > 0;
  }
  /**
   * @description Method to determine if this list contains FulfillableItem with delegationType set to any non-null value <> 'NA'
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasDelegationType() {
    return this.items.some(item => !!item.delegationType && item.delegationType !== FULFILLABLE_ITEM_DELEGATION_TYPE.NA);
  }
  /**
   * @description Method to determine if list contains desktop type items.
   *
   * @returns {Boolean} true if the list has desktop items, otherwise false
   */


  hasDesktopTypeItems() {
    return this.items.some(item => item.fulfillableItemType === FULFILLABLE_ITEM_TYPE.DESKTOP);
  }
  /**
   * @description Method to determine if this list contains the DMA ACP CS FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasDmaAcpCs() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.DMA_ACP_CS);
  }
  /**
   * @description Method to determine if this list contains the domain_claiming FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasDomainClaiming() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.DOMAIN_CLAIMING);
  }
  /**
   * @description Method to determine if this list contains the ESM shared storage FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasESMSharedStorage() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.ESM_SHARED_STORAGE);
  }
  /**
   * @description Method to determine if this list contains the ESM user storage FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasESMUserStorage() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.ESM_USER_STORAGE);
  }
  /**
   * @description Method to determine if this list has a support case creation FulfillableItem and
   * does not have a disallow expert session FulfillableItem.
   *
   * @returns {Boolean} True if disallow_expert_sessions does not present, false otherwise.
   */


  hasExpertSessionCreationAllowed() {
    return this.hasSupportCaseCreationAllowed() && !this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.EXPORT_SESSION_CREATION_DISALLOWED);
  }
  /**
   * @description Method to check if given fiCode exists in FulfillableItem list
   *
   * @param {string} [fiCode] fulfillableItem code
   * @returns {boolean} True if the fiCode present in list
   */


  hasFulfillableItemCode(fiCode) {
    return this.items.some(fi => fi.code === fiCode);
  }
  /**
   * @description Method to determine if this list contains the laboratory_license_mgmt FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasLaboratoryLicenseManagement() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.LABORATORY_LICENSE_MANAGEMENT);
  }
  /**
   * @description Method to determine if this list contains a quota organization delegatable FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasOrgDelegatable() {
    const fiCodeAllowlist = ['sbst_source'];
    const consumableQuotaItems = this.getConsumableQuotaTypeItems({
      requireDelegationConfigurable: false
    });
    return consumableQuotaItems.filter(item => fiCodeAllowlist.includes(item.code)).some(item => item.delegationType === FULFILLABLE_ITEM_DELEGATION_TYPE.ORGANIZATION && item.fulfillableItemType === FULFILLABLE_ITEM_TYPE.QUOTA);
  }
  /**
   * @description Method to determine if this list contains an organization type quota on-demand consumable FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasOrgOnDemandConsumable() {
    return !!this.getOrgOnDemandConsumableItem();
  }
  /**
   * @description Method to determine if this list contains an organization type quota recurring consumable FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasOrgRecurringConsumable() {
    return this.items.some(item => item.delegationType === FULFILLABLE_ITEM_DELEGATION_TYPE.ORGANIZATION && item.fulfillableItemType === FULFILLABLE_ITEM_TYPE.QUOTA && item.chargingModel && item.chargingModel.model === FULFILLABLE_ITEM_CHARGING_MODEL_TYPE.RECURRING);
  }
  /**
   * @description Method to determine if this list contains the overdelegation_allowed FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasOverdelegationAllowed() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.OVERDELEGATION_ALLOWED);
  }
  /**
   * @description Method to determine if this list contains the packaging_preconditioning FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasPackagePreconditioning() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.PACKAGE_PRECONDITIONING);
  }
  /**
   * @description Method to return whether the list has fulfillable items of quota type.
   * @param {Boolean} [requireDelegationConfigurable] true if only configurable items
   *   should be returned. Defaults to true.
   *
   * @returns {Boolean} true if any fulfillable item is of type quota
   */


  hasQuotaTypeItems(requireDelegationConfigurable = true) {
    return this.getQuotaTypeItems(requireDelegationConfigurable).length > 0;
  }
  /**
   * @description Method to return whether the list has fulfillable items of service type.
   * @param {Boolean} [requireDelegationConfigurable] true if only configurable items
   *   should be returned. Defaults to true.
   *
   * @returns {Boolean} true if any fulfillable item is of type service
   */


  hasServiceTypeItems(requireDelegationConfigurable = true) {
    return this.getServiceTypeItems(requireDelegationConfigurable).length > 0;
  }
  /**
   * @description Method to determine if this list contains the total_sign_transaction FulfillableItem, optionally, with
   *   the specified chargingModel unit.
   *
   * @param {String} [unit] - If specified, the charging model unit must match too.
   * @returns {Boolean} true if this list has the FulfillableItem with the optionally specified charging model unit, else false
   */


  hasSignTransactions(unit) {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.TOTAL_SIGN_TRANSACTIONS && (!unit || item.chargingModel && item.chargingModel.unit === unit));
  }
  /**
   * @description Method to determine if this list contains the single desktop
   *   application config FulfillableItem.
   *
   * @returns {Boolean} True if the FulfillableItem is present, false otherwise.
   */


  hasSingleDesktopApplicationConfig() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.SINGLE_DESKTOP_APPLICATION_CONFIGURATION);
  }
  /**
   * @description Method to determine if this list has a credit quota.
   *
   * @returns {Boolean} true if this list has a credit quota, else false
   */


  hasStockCredits() {
    return this.getStockCreditChargingModelCap() >= 0;
  }
  /**
   * @description Method to determine if this list has an image quota.
   *
   * @returns {Boolean} true if this list has an image quota, else false
   */


  hasStockImages() {
    return this.getStockImageChargingModelCap() >= 0;
  }
  /**
   * @description Method to determine if this list has a video quota.
   *
   * @returns {Boolean} true if this list has a video quota, else false
   */


  hasStockVideos() {
    return this.getStockVideoChargingModelCap() >= 0;
  }
  /**
   * @description Method to determine if this list has a supported product FulfillableItem.
   *
   * @returns {Boolean} true if present, else false.
   */


  hasSupportedProduct() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.SUPPORTED_PRODUCT);
  }
  /**
   * @description Method to determine if this list has a support allowed admins FulfillableItem.
   *
   * @returns {Boolean} true if present, else false.
   */


  hasSupportAllowedAdmins() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.SUPPORT_ALLOWED_ADMINS);
  }
  /**
   * @description Method to determine if this list has a support case creation FulfillableItem.
   *
   * @returns {Boolean} True if present, false otherwise.
   */


  hasSupportCaseCreationAllowed() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.SUPPORT_CASE_CREATION_ALLOWED);
  }
  /**
   * @description Method to determine if this list has a support expert session FulfillableItem.
   *
   * @returns {Boolean} true if present, else false.
   */


  hasSupportExpertSession() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.SUPPORT_EXPERT_SESSION);
  }
  /**
   * @description Method to determine if this list has a support FulfillableItemType.
   *
   * @returns {Boolean} True if present, false otherwise.
   */


  hasSupportFulfillableItemType() {
    return this.items.some(item => item.fulfillableItemType === FULFILLABLE_ITEM_TYPE.SUPPORT);
  }
  /**
   * @description Method to determine if this list has a support role assignment FulfillableItem.
   *
   * @returns {Boolean} true if present, else false.
   */


  hasSupportRoleAssignmentAllowed() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.SUPPORT_ROLE_ASSIGNMENT_ALLOWED);
  }
  /**
   * @description Method to determine if there are any unsaved changes to this list.
   *
   * @returns {Boolean} true if there are unsaved changes, else false
   */


  hasUnsavedChanges() {
    return !isEqual_1.default(this.items, this.savedState);
  }
  /**
   * @description Method to determine if this list contains User Permission Management.
   *
   * @returns {Boolean} true if the FulfillableItem is present, otherwise false.
   */


  hasUserGroupAssignment() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.USER_GROUP_ASSIGNMENT);
  }
  /**
   * @description Method to determine if this list contains User Permission Management.
   *
   * @returns {Boolean} true if the FulfillableItem is present, otherwise false.
   */


  hasUserPermissionManagement() {
    return this.items.some(item => item.code === FULFILLABLE_ITEM_CODE.USER_PERMISSION_MANAGEMENT);
  }

}

exports.FulfillableItemList = FulfillableItemList;

function extractQuotaChargingModelCap(fulfillableItem) {
  var _a;

  const chargingModelCap = (_a = fulfillableItem === null || fulfillableItem === void 0 ? void 0 : fulfillableItem.chargingModel) === null || _a === void 0 ? void 0 : _a.cap;

  if (chargingModelCap && chargingModelCap !== FULFILLABLE_ITEM_CHARGING_MODEL_CAP_UNLIMITED) {
    // Unary operator is used to convert a string to a number.
    return +chargingModelCap;
  }

  return undefined;
}
/**
 * @description Initializes Fulfillable Items
 *
 * @param {Array} items Fulfillable Items to be processed
 *
 * @returns {Array} the processed items
 */


function parse(items) {
  const processedItems = items; // Behance/Portfolio are really only a single service, which the offers service
  // wants to break apart, we need to merge the icons back together

  const behanceService = processedItems.find(item => item.code === FULFILLABLE_ITEM_CODE.BEHANCE);
  const portfolioService = processedItems.find(item => item.code === FULFILLABLE_ITEM_CODE.PORTFOLIO);

  if (behanceService && portfolioService) {
    const behanceIcons = behanceService.assets && behanceService.assets.icons;
    const portfolioSvg = portfolioService.assets && portfolioService.assets.icons && portfolioService.assets.icons.svg;

    if (behanceIcons && portfolioSvg) {
      behanceIcons.secondarySvg = portfolioSvg;
    }
  }

  return processedItems;
}